import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiSearch, type TokenSource } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * The commission tracker (167, P-24): one line per ACTING order, its steps read from the records and its state the
 * first unmet step. Nobody sets a state (Q23, D-128): a person records an APPLICATION or opens a BLOCKER, and the
 * oath, the bond and the commission keep their own records (154, 162). api/src/routes/commissions.ts.
 */

/** A line's state, the first unmet step (commission_lines()). */
export type CommissionLineState =
  | 'no_acting_order' | 'blocked' | 'waiting_oath_bond' | 'waiting_bond_unknown' | 'waiting_application'
  | 'applied_for' | 'commissioned' | 'commissioned_by_order';

export type BlockerReason = 'AWAITING_FINAL_OJ' | 'DOCUMENTS_UNCLEAR' | 'NOT_EFILED';
export const BLOCKER_REASONS: { code: BlockerReason; label: string; hint: string }[] = [
  { code: 'NOT_EFILED', label: 'Not e-filed', hint: 'A court trip must take place: a task is made for it.' },
  { code: 'AWAITING_FINAL_OJ', label: 'Awaiting the final O&J', hint: 'Waiting on the court: no task. It clears when the final order is recorded.' },
  { code: 'DOCUMENTS_UNCLEAR', label: 'Documents unclear', hint: 'A meeting with Legal: a task is made for the Legal queue.' },
];

export interface CommissionLine {
  client_id: string;
  client_ref: string;
  display_name: string;
  legal_status: string | null;
  county_code: string | null;
  needs_commission: boolean;
  /** Null on the red line: a client the tracker covers with no acting order. */
  court_order_id: string | null;
  order_words: string | null;
  order_signed: string | null;
  is_temporary: boolean | null;
  oath_met: boolean;
  oath_on: string | null;
  bond_required: boolean | null;
  bond_required_source: 'order' | 'client' | 'tracker' | null;
  bond_met: boolean;
  bond_issued_on: string | null;
  /** P7: a standing issued bond on file for the client, but on no order or another one. */
  bond_elsewhere: boolean;
  applied_for: boolean;
  applied_on: string | null;
  application_id: string | null;
  blocker_id: string | null;
  blocker_code: BlockerReason | null;
  blocker_label: string | null;
  blocker_opened: string | null;
  blocker_note: string | null;
  commissioned: boolean;
  commissioned_by_order: boolean;
  commissioned_on: string | null;
  state: CommissionLineState;
  /** The state in words, as the database says it. */
  waiting_words: string;
  /** The bond beside the step, as information, never the state. */
  bond_words: string | null;
  filing_method: string | null;
  filing_method_overridden: boolean;
  /** The tracker's only: days since the order was signed. */
  age_days?: number | null;
}

export interface CommissionFilters {
  /** One state's lines. The headline figures do not move when this changes. */
  state?: string;
  county?: string;
  /** Clients whose appointment needs no commission are hidden unless this says otherwise. */
  kinds?: 'show';
  sort: string;
}

export interface CommissionPage {
  /** X: clients whose every acting order is commissioned. */
  clients_commissioned: number;
  /** Y: acting orders not yet commissioned. The number watched. */
  orders_pending: number;
  /** The red lines: covered clients with no acting order. */
  no_acting_order: number;
  blocked: number;
  not_in_the_race: number;
  kinds_shown: boolean;
  by_state: { state: CommissionLineState; sort_order: number; lines: number }[];
  states: { state: CommissionLineState; label: string }[];
  counties: { county_code: string; lines: number }[];
  may_record: boolean;
  may_resolve: boolean;
  may_see_no_order: boolean;
  total: number;
  limit: number | null;
  offset: number;
  sort: string;
  lines: CommissionLine[];
}

export interface CommissionBlocker {
  id: string;
  court_order_id: string | null;
  reason_code: BlockerReason;
  reason_label: string;
  blocks_step: 'oath_designation' | 'application' | null;
  note: string;
  opened_on: string;
  cleared_on: string | null;
  cleared_by: 'oath_designation' | 'application' | 'final_order' | 'resolved' | null;
  cleared_note: string | null;
  withdrawn: boolean;
  withdrawal_reason: string | null;
  versions: number;
}

export interface CommissionApplication {
  id: string;
  court_order_id: string;
  applied_on: string;
  upload_id: string | null;
  clio_copy_id: string | null;
  withdrawn: boolean;
  withdrawal_reason: string | null;
  versions: number;
}

export interface ClientCommission {
  lines: CommissionLine[];
  blockers: CommissionBlocker[];
  applications: CommissionApplication[];
  may_record: boolean;
  may_resolve: boolean;
}

/** The tracker: every line on one scrolling page, as Billing is (no limit is sent). */
export function useCommissionTracker(filters: CommissionFilters) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['dashboards', 'commission', filters],
    queryFn: () => apiSearch<CommissionPage>('/dashboards/commission', filters, getToken),
    ...queryDefaults,
    placeholderData: (previous) => previous,
  });
}

/** The client's lines, blockers and applications, for the Legal section. */
export function useClientCommission(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'commission'],
    queryFn: () => apiFetch<ClientCommission>(`/clients/${clientId}/commission`, getToken),
    enabled,
    ...queryDefaults,
  });
}

/** Anything recorded moves the client between counts and states: the tracker and the client are both refetched. */
function useCommissionWrite<V extends { clientId: string }>(send: (v: V, token: TokenSource) => Promise<unknown>) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => send(v, getToken),
    onSuccess: (_r, v) => {
      void queryClient.invalidateQueries({ queryKey: ['dashboards', 'commission'] });
      void queryClient.invalidateQueries({ queryKey: ['client', v.clientId] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

const post = (body: unknown) => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export function useRecordApplication() {
  return useCommissionWrite<{ clientId: string; orderId: string; applied_on: string }>((v, t) =>
    apiFetch<{ id: string }>(`/court_orders/${v.orderId}/commission_application`, t, post({ applied_on: v.applied_on })));
}

export function useWithdrawApplication() {
  return useCommissionWrite<{ clientId: string; applicationId: string; reason: string }>((v, t) =>
    apiFetch<{ version: number }>(`/commission_applications/${v.applicationId}/withdraw`, t, post({ reason: v.reason })));
}

export function useOpenBlocker() {
  return useCommissionWrite<{ clientId: string; orderId: string | null; reason_code: BlockerReason; note: string }>((v, t) =>
    apiFetch<{ id: string }>(`/clients/${v.clientId}/commission_blockers`, t,
      post({ court_order_id: v.orderId, reason_code: v.reason_code, note: v.note })));
}

export function useResolveBlocker() {
  return useCommissionWrite<{ clientId: string; blockerId: string; reason: string }>((v, t) =>
    apiFetch<{ resolved: boolean }>(`/commission_blockers/${v.blockerId}/resolve`, t, post({ reason: v.reason })));
}

export function useWithdrawBlocker() {
  return useCommissionWrite<{ clientId: string; blockerId: string; reason: string }>((v, t) =>
    apiFetch<{ version: number }>(`/commission_blockers/${v.blockerId}/withdraw`, t, post({ reason: v.reason })));
}
